/**
 * Utilities for extracting OpenSCAD code into separate files.
 *
 * Handles two cases:
 *   1. A complete module definition is selected → move the module as-is and replace with a call
 *   2. Arbitrary code is selected → wrap it in a new module, detect used outer variables as params
 *
 * Also supports bulk extraction:
 *   3. No selection → scan the whole file, extract every top-level module to its own file
 */

export type SelectionType = 'module' | 'code';

export interface ExtractPreview {
  selectionType: SelectionType;
  /** The full content to write to the new .scad file */
  fileContent: string;
  /** The module call (or include-only) that replaces the selected text in the main file */
  callCode: string;
  /** The include statement to insert at the top of the main file */
  importStatement: string;
  /** Suggested module name derived from the extracted module (or generated) */
  suggestedModuleName: string;
}

// ---------------------------------------------------------------------------
// Detection helpers
// ---------------------------------------------------------------------------

/**
 * Returns 'module' if the selection is a complete OpenSCAD module definition,
 * otherwise 'code'.
 *
 * A module definition looks like:
 *   module name(...) { ... }
 * optionally preceded by whitespace / comments.
 */
export function detectSelectionType(selectedText: string): SelectionType {
  const trimmed = selectedText.trim();
  // Must start with the 'module' keyword followed by a valid identifier
  if (!/^module\s+[a-zA-Z_$][a-zA-Z0-9_$]*\s*\(/.test(trimmed)) {
    return 'code';
  }

  // Verify that the braces are balanced (i.e. it's a complete definition)
  let depth = 0;
  let foundOpen = false;
  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (ch === '{') {
      depth++;
      foundOpen = true;
    } else if (ch === '}') {
      depth--;
      if (foundOpen && depth === 0) {
        // Make sure there's nothing meaningful after the closing brace
        const rest = trimmed.slice(i + 1).trim();
        if (rest === '' || rest.startsWith('//') || rest.startsWith('/*')) {
          return 'module';
        }
        // If there's more content it might be multiple definitions – treat as code
        return 'code';
      }
    }
  }
  return 'code';
}

/**
 * Extract the module name from a module definition string.
 * e.g. "module gear(teeth=20) { ... }" → "gear"
 */
export function extractModuleName(moduleText: string): string {
  const match = moduleText.trim().match(/^module\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/);
  return match ? match[1] : 'extracted';
}

/**
 * Extract the parameter list string from a module definition.
 * e.g. "module gear(teeth=20, r=5) { ... }" → "teeth=20, r=5"
 */
export function extractModuleParams(moduleText: string): string {
  const trimmed = moduleText.trim();
  const parenOpen = trimmed.indexOf('(');
  if (parenOpen === -1) return '';

  let depth = 0;
  let close = -1;
  for (let i = parenOpen; i < trimmed.length; i++) {
    if (trimmed[i] === '(') depth++;
    else if (trimmed[i] === ')') {
      depth--;
      if (depth === 0) {
        close = i;
        break;
      }
    }
  }
  if (close === -1) return '';
  return trimmed.slice(parenOpen + 1, close).trim();
}

// ---------------------------------------------------------------------------
// Variable detection helpers
// ---------------------------------------------------------------------------

// OpenSCAD built-in names that should NOT be treated as outer variables
const SCAD_BUILTINS = new Set([
  'true',
  'false',
  'undef',
  'PI',
  'cube',
  'sphere',
  'cylinder',
  'polyhedron',
  'polygon',
  'circle',
  'square',
  'translate',
  'rotate',
  'scale',
  'mirror',
  'resize',
  'multmatrix',
  'color',
  'material',
  'material_def',
  'offset',
  'union',
  'difference',
  'intersection',
  'hull',
  'minkowski',
  'linear_extrude',
  'rotate_extrude',
  'projection',
  'import',
  'surface',
  'text',
  'echo',
  'render',
  'use',
  'include',
  'for',
  'if',
  'else',
  'let',
  'function',
  'module',
  'abs',
  'ceil',
  'floor',
  'round',
  'sqrt',
  'pow',
  'exp',
  'log',
  'sin',
  'cos',
  'tan',
  'asin',
  'acos',
  'atan',
  'atan2',
  'max',
  'min',
  'norm',
  'cross',
  'concat',
  'len',
  'str',
  'lookup',
  'search',
  'version',
  'version_num',
  'parent_module',
  'is_undef',
  'is_bool',
  'is_num',
  'is_string',
  'is_list',
  'assert',
  'sign',
  'sum',
  '$fn',
  '$fa',
  '$fs',
  '$t',
  '$vpr',
  '$vpt',
  '$vpd',
  '$vpf',
  '$children',
  '$preview',
]);

/**
 * Collect all identifier references used in a piece of code.
 * Returns a deduplicated array of names.
 */
function collectIdentifiers(code: string): string[] {
  const seen = new Set<string>();
  // Strip string literals and comments first to avoid false positives
  const stripped = stripStringsAndComments(code);
  const regex = /\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(stripped)) !== null) {
    seen.add(m[1]);
  }
  return Array.from(seen);
}

/**
 * Replace string literals and comments with whitespace so identifier
 * detection doesn't pick up words inside them.
 */
function stripStringsAndComments(code: string): string {
  // Remove block comments
  let result = code.replace(/\/\*[\s\S]*?\*\//g, (m) => ' '.repeat(m.length));
  // Remove line comments
  result = result.replace(/\/\/[^\n]*/g, (m) => ' '.repeat(m.length));
  // Remove string literals
  result = result.replace(/"(?:[^"\\]|\\.)*"/g, (m) => ' '.repeat(m.length));
  return result;
}

/**
 * Collect top-level variable names assigned in the code (outside of any module/function body).
 * e.g. "width = 10;" → "width"
 */
export function parseOuterVariables(
  fullCode: string,
  excludeStartLine: number,
  excludeEndLine: number,
): string[] {
  const lines = fullCode.split('\n');
  const names: string[] = [];
  let depth = 0;

  // Simple regex for top-level variable assignments
  const assignRegex = /^(\$?[a-zA-Z_][a-zA-Z0-9_$]*)\s*=/;

  for (let i = 0; i < lines.length; i++) {
    const lineNum = i + 1; // 1-indexed
    const line = lines[i];

    // Count brace depth changes on this line (simplified)
    for (const ch of line) {
      if (ch === '{') depth++;
      else if (ch === '}') depth--;
    }

    // Skip lines within the selection itself
    if (lineNum >= excludeStartLine && lineNum <= excludeEndLine) continue;

    // Only consider top-level (depth 0) assignments
    if (depth === 0) {
      const trimmed = line.trim();
      if (trimmed.startsWith('//') || trimmed.startsWith('/*')) continue;
      const m = trimmed.match(assignRegex);
      if (m && !SCAD_BUILTINS.has(m[1])) {
        if (!names.includes(m[1])) {
          names.push(m[1]);
        }
      }
    }
  }

  return names;
}

/**
 * From a list of identifier references found in the selected code,
 * return only those that match known outer variable names (and are not builtins).
 */
export function detectUsedVariables(
  selectedCode: string,
  outerVariables: string[],
): string[] {
  const identifiers = collectIdentifiers(selectedCode);
  return outerVariables.filter(
    (v) => identifiers.includes(v) && !SCAD_BUILTINS.has(v),
  );
}

// ---------------------------------------------------------------------------
// Code generation
// ---------------------------------------------------------------------------

/**
 * Convert a filename (without extension) to a valid OpenSCAD module name.
 * e.g. "gear-part" → "gear_part", "my file" → "my_file"
 */
export function filenameToModuleName(filename: string): string {
  return filename
    .replace(/\.scad$/i, '')
    .replace(/[^a-zA-Z0-9_$]/g, '_')
    .replace(/^(\d)/, '_$1'); // can't start with digit
}

/**
 * Build the extracted file content and the replacement code for the main file.
 *
 * @param selectedCode   The text the user selected
 * @param selectionType  Whether it's a module definition or arbitrary code
 * @param usedVars       Variables from outer scope used in the selected code (only for 'code' case)
 * @param moduleName     The module name to use for wrapping (for 'code' case)
 */
export function buildExtractedContent(
  selectedCode: string,
  selectionType: SelectionType,
  usedVars: string[],
  moduleName: string,
): { fileContent: string; callCode: string } {
  if (selectionType === 'module') {
    // The file content is the module definition as-is.
    // The definition is simply removed from the main file (not replaced with a
    // call) — the include statement at the top makes the module available there.
    const fileContent = selectedCode.trimEnd() + '\n';
    const callCode = '';

    return { fileContent, callCode };
  } else {
    // Wrap the selected code in a module
    const indented = indentCode(selectedCode.trim(), '  ');
    const paramDecl = usedVars.map((v) => `${v} = ${v}`).join(', ');
    const fileContent = `module ${moduleName}(${paramDecl}) {\n${indented}\n}\n`;

    const callArgs = usedVars.map((v) => `${v}=${v}`).join(', ');
    const callCode = `${moduleName}(${callArgs});`;

    return { fileContent, callCode };
  }
}

/**
 * Given a module's parameter declaration string (e.g. "teeth=20, r=5"),
 * produce call arguments that pass each named param by name (e.g. "teeth=teeth, r=r").
 * If a param has no default it's kept positional-style.
 */
function buildModuleCallParams(paramsStr: string): string {
  if (!paramsStr.trim()) return '';

  // Split on commas not inside brackets
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of paramsStr) {
    if (ch === '[' || ch === '(') depth++;
    else if (ch === ']' || ch === ')') depth--;
    else if (ch === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.trim()) parts.push(current.trim());

  // For each parameter, extract the name and produce name=name
  return parts
    .map((p) => {
      const eqIdx = p.indexOf('=');
      const name = eqIdx !== -1 ? p.slice(0, eqIdx).trim() : p.trim();
      return name ? `${name}=${name}` : p;
    })
    .join(', ');
}

/**
 * Indent every non-empty line of code by a given prefix.
 */
function indentCode(code: string, prefix: string): string {
  return code
    .split('\n')
    .map((line) => (line.trim() === '' ? '' : prefix + line))
    .join('\n');
}

/**
 * Build the full import statement for a .scad file.
 */
export function buildImportStatement(filename: string): string {
  return `include <${filename}>`;
}

// ---------------------------------------------------------------------------
// Bulk module extraction
// ---------------------------------------------------------------------------

export interface ModuleSpan {
  /** The module name */
  name: string;
  /** 1-indexed start line of the leading comment block, or same as moduleStartLine if none */
  startLine: number;
  /** 1-indexed start line of the "module" keyword line */
  moduleStartLine: number;
  /** 1-indexed end line (the closing '}') */
  endLine: number;
  /** The full text of the module definition, including any leading comments */
  text: string;
}

export interface BulkExtractPayload {
  /** New filename for the extracted module, e.g. "gear.scad" */
  filename: string;
  /** Content to write to that file */
  fileContent: string;
  /** The call that replaces the module definition in the main file */
  callCode: string;
  /** The include statement to add at the top of the main file */
  importStatement: string;
  /** 1-indexed span of the module definition in the *original* source */
  startLine: number;
  endLine: number;
}

/**
 * Scan `code` and return a list of all top-level module definitions with their
 * line spans and full text.
 *
 * Top-level means depth-0 in brace nesting (not nested inside another module).
 */
export function scanModules(code: string): ModuleSpan[] {
  const lines = code.split('\n');
  const modules: ModuleSpan[] = [];

  // Track brace depth outside the current module under construction
  let outerDepth = 0;
  // When we find a module keyword, start collecting
  let collecting = false;
  let moduleStartLine = -1;
  let commentStartLine = -1;
  let moduleName = '';
  let collectDepth = 0; // brace depth relative to the start of the module
  let collectLines: string[] = [];

  const moduleStartRe = /^\s*module\s+([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(/;

  /**
   * Walk backwards from `fromLine` (0-indexed) and collect any contiguous
   * comment lines immediately preceding the module keyword.  Returns the
   * 0-indexed index of the first comment line found, or `fromLine` if none.
   */
  function findCommentStart(fromLine: number): number {
    let inBlockComment = false;
    let start = fromLine;
    for (let j = fromLine - 1; j >= 0; j--) {
      const t = lines[j].trim();
      if (inBlockComment) {
        // Scanning backwards inside a block comment — look for the opening /*
        if (t.startsWith('/*') || t.includes('/*')) {
          start = j;
          inBlockComment = false;
        } else {
          start = j;
        }
      } else if (t.endsWith('*/')) {
        // End of a block comment — start scanning backwards for the opening
        inBlockComment = true;
        start = j;
      } else if (t.startsWith('//')) {
        start = j;
      } else if (t === '') {
        // A blank line breaks the comment chain
        break;
      } else {
        break;
      }
    }
    return start;
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineNum = i + 1; // 1-indexed

    if (!collecting) {
      // Check whether this line starts a module at depth 0
      if (outerDepth === 0 && moduleStartRe.test(line)) {
        const m = line.match(moduleStartRe)!;
        moduleName = m[1];
        moduleStartLine = lineNum;
        collecting = true;
        collectDepth = 0;

        // Walk back to find any leading comment block
        const commentStartIdx = findCommentStart(i); // 0-indexed
        commentStartLine = commentStartIdx + 1; // 1-indexed
        collectLines = lines.slice(commentStartIdx, i + 1);

        // Count braces on this first line
        for (const ch of line) {
          if (ch === '{') collectDepth++;
          else if (ch === '}') collectDepth--;
        }

        // Edge case: entire module on one line
        if (collectDepth === 0 && collectLines.join('\n').includes('{')) {
          modules.push({
            name: moduleName,
            startLine: commentStartLine,
            moduleStartLine,
            endLine: lineNum,
            text: collectLines.join('\n'),
          });
          collecting = false;
        }
      } else {
        // Not collecting — track outer brace depth so we don't mistake
        // nested modules for top-level ones.
        for (const ch of line) {
          if (ch === '{') outerDepth++;
          else if (ch === '}') outerDepth--;
        }
      }
    } else {
      // We are collecting a module body
      if (lineNum === moduleStartLine) {
        // Already processed above
      } else {
        collectLines.push(line);
        for (const ch of line) {
          if (ch === '{') collectDepth++;
          else if (ch === '}') collectDepth--;
        }
      }

      if (collectDepth === 0) {
        modules.push({
          name: moduleName,
          startLine: commentStartLine,
          moduleStartLine,
          endLine: lineNum,
          text: collectLines.join('\n'),
        });
        collecting = false;
        outerDepth = 0;
      }
    }
  }

  return modules;
}

/**
 * Given the full source code, build one `BulkExtractPayload` per top-level
 * module.  Modules that don't have a body (collectDepth never closes) are
 * silently skipped.
 *
 * Each payload contains enough information to:
 *  - write a new `.scad` file
 *  - replace the module definition in the main file with a call
 *  - add an `include` statement at the top of the main file
 */
export function buildExtractAllModulesPayloads(
  code: string,
): BulkExtractPayload[] {
  const modules = scanModules(code);
  return modules.map((mod) => {
    const filename = `${mod.name}.scad`;
    const fileContent = mod.text.trimEnd() + '\n';
    // The definition is removed from the main file; the include at the top is
    // sufficient to make the module available.
    const callCode = '';
    const importStatement = buildImportStatement(filename);
    return {
      filename,
      fileContent,
      callCode,
      importStatement,
      startLine: mod.startLine,
      endLine: mod.endLine,
    };
  });
}

/**
 * Public wrapper around the private `buildModuleCallParams` so that
 * `buildExtractAllModulesPayloads` can use it without duplication.
 */
function buildModuleCallParamsPublic(paramsStr: string): string {
  if (!paramsStr.trim()) return '';
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of paramsStr) {
    if (ch === '[' || ch === '(') depth++;
    else if (ch === ']' || ch === ')') depth--;
    else if (ch === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.trim()) parts.push(current.trim());
  return parts
    .map((p) => {
      const eqIdx = p.indexOf('=');
      const name = eqIdx !== -1 ? p.slice(0, eqIdx).trim() : p.trim();
      return name ? `${name}=${name}` : p;
    })
    .join(', ');
}

// ---------------------------------------------------------------------------
// Extract-to-variable utilities
// ---------------------------------------------------------------------------

/**
 * The inferred type of a selected literal value.
 *
 * - 'number'   : a single numeric literal, e.g. 42 or 3.14
 * - 'boolean'  : true or false
 * - 'string'   : a double-quoted string literal, e.g. "box"
 * - 'number[]' : a vector of numbers, e.g. [10, 20, 30]
 * - 'color'    : a 3 or 4-element float vector with values 0–1, or a CSS/OpenSCAD color string
 * - 'unknown'  : not a recognisable literal — don't offer Extract to Variable
 */
export type LiteralType =
  | 'number'
  | 'boolean'
  | 'string'
  | 'number[]'
  | 'color'
  | 'unknown';

/** A detected literal with its inferred type and element count for vectors. */
export interface DetectedLiteral {
  type: LiteralType;
  /** Number of elements for number[] / color vectors, otherwise undefined */
  elementCount?: number;
}

/**
 * Infer the type of a raw literal string selected in the editor.
 *
 * Handles:
 *   - booleans: "true", "false"
 *   - numbers: "42", "3.14", "-1.5"
 *   - quoted strings: "\"hello\""
 *   - numeric vectors: "[10, 20, 30]"
 *   - color vectors: "[0.2, 0.8, 0.4]" (3/4 float elements all 0–1)
 */
export function detectLiteralType(raw: string): DetectedLiteral {
  const s = raw.trim();

  if (s === 'true' || s === 'false') return { type: 'boolean' };

  if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(s)) return { type: 'number' };

  if (s.startsWith('"') && s.endsWith('"') && s.length >= 2)
    return { type: 'string' };

  if (s.startsWith('[') && s.endsWith(']')) {
    const inner = s.slice(1, -1).trim();
    // Must not contain nested brackets (avoid matching complex expressions)
    if (!inner.includes('[') && !inner.includes(']')) {
      const parts = inner.split(',').map((p) => p.trim());
      if (
        parts.length >= 2 &&
        parts.every((p) => /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(p))
      ) {
        const nums = parts.map(Number);
        const elementCount = nums.length;
        // Colour vector: 3 or 4 elements all in [0, 1]
        if (
          (elementCount === 3 || elementCount === 4) &&
          nums.every((n) => n >= 0 && n <= 1)
        ) {
          return { type: 'color', elementCount };
        }
        return { type: 'number[]', elementCount };
      }
    }
  }

  return { type: 'unknown' };
}

/**
 * Suggest a variable name by inspecting the code context immediately
 * before the literal at `selectionStartOffset`.
 *
 * Heuristics (in order):
 *  1. Named argument:  `ident = <literal>` or `ident=<literal>` → use ident
 *  2. Otherwise fall back to "value"
 *
 * The suggestion is sanitised to a valid OpenSCAD identifier.
 */
export function suggestVariableNameForLiteral(
  code: string,
  selectionStartOffset: number,
  _literal: string,
): string {
  // Grab up to 60 characters before the selection on the same line
  const lineStart = code.lastIndexOf('\n', selectionStartOffset - 1) + 1;
  const prefix = code.slice(lineStart, selectionStartOffset);

  // Pattern: identifier (possibly with $) followed by optional spaces then '='
  // We look for the last such token before the literal.
  const m = prefix.match(/(\$?[a-zA-Z_][a-zA-Z0-9_$]*)\s*=\s*$/);
  if (m) {
    const name = m[1];
    // Make sure it is not a keyword / builtin
    if (!SCAD_BUILTINS.has(name)) {
      return name;
    }
  }

  return 'value';
}

/**
 * Build a token-boundary regex that matches the literal exactly, without
 * matching it as a substring of a larger token.
 *
 * Works for: numbers, booleans, quoted strings, and numeric vectors.
 */
function buildLiteralRegex(literal: string): RegExp {
  const s = literal.trim();

  if (s.startsWith('"')) {
    // Quoted string — escape for regex and require exact match
    const escaped = s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(escaped, 'g');
  }

  if (s.startsWith('[')) {
    // Vector — escape brackets and internal spaces loosely
    const inner = s.slice(1, -1);
    const escapedInner = inner
      .split(',')
      .map((p) => p.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('\\s*,\\s*');
    return new RegExp(`\\[\\s*${escapedInner}\\s*\\]`, 'g');
  }

  // Number or boolean — require word boundaries
  const escaped = s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?<![a-zA-Z0-9_$.])${escaped}(?![a-zA-Z0-9_$.])`, 'g');
}

/**
 * Count occurrences of `literal` in `code` that are NOT inside comments or
 * (for non-string literals) inside string literals, excluding the one at
 * `ownOffset` (the selection position).
 *
 * Returns the count of *other* occurrences.
 */
export function countOtherLiteralOccurrences(
  code: string,
  literal: string,
  ownOffset: number,
): number {
  const regex = buildLiteralRegex(literal);
  // Build a stripped version for testing which matches are in comments/strings.
  // We keep a parallel "masked" string where comments and strings are replaced
  // by spaces so we can check if an offset is safe.
  const masked = maskCommentsAndStrings(code);

  let count = 0;
  let m: RegExpExecArray | null;
  regex.lastIndex = 0;
  while ((m = regex.exec(code)) !== null) {
    const start = m.index;
    const end = start + m[0].length;

    // Skip if inside a comment or string
    if (masked[start] !== code[start]) continue;

    // Skip the own occurrence (the selection itself)
    if (start <= ownOffset && ownOffset < end) continue;
    // Also skip if the selection start falls anywhere in this match range
    if (ownOffset >= start && ownOffset < end) continue;

    count++;
  }
  return count;
}

/**
 * Return a copy of `code` where block comments, line comments, and string
 * literals are replaced with spaces.  The result has the same length and
 * character positions as the original, so offset comparisons remain valid.
 */
function maskCommentsAndStrings(code: string): string {
  const chars = code.split('');
  let i = 0;
  while (i < chars.length) {
    if (code[i] === '/' && code[i + 1] === '*') {
      // Block comment
      const end = code.indexOf('*/', i + 2);
      const stop = end === -1 ? chars.length : end + 2;
      for (let j = i; j < stop; j++) {
        if (chars[j] !== '\n') chars[j] = ' ';
      }
      i = stop;
    } else if (code[i] === '/' && code[i + 1] === '/') {
      // Line comment
      let j = i;
      while (j < chars.length && chars[j] !== '\n') {
        chars[j] = ' ';
        j++;
      }
      i = j;
    } else if (code[i] === '"') {
      // String literal
      chars[i] = ' ';
      let j = i + 1;
      while (j < chars.length) {
        if (code[j] === '\\') {
          chars[j] = ' ';
          j++;
          if (j < chars.length) {
            chars[j] = ' ';
            j++;
          }
        } else if (code[j] === '"') {
          chars[j] = ' ';
          j++;
          break;
        } else {
          if (chars[j] !== '\n') chars[j] = ' ';
          j++;
        }
      }
      i = j;
    } else {
      i++;
    }
  }
  return chars.join('');
}

/**
 * Find the 1-indexed line number where a new top-level variable should be
 * inserted so that it appears after the last existing top-level assignment.
 *
 * Returns 1 when there are no existing top-level assignments (insert at top).
 */
export function findInsertionLineForVariable(code: string): number {
  const lines = code.split('\n');
  let depth = 0;
  let lastAssignLine = 0; // 0 means "none found"

  const assignRegex = /^\s*(\$?[a-zA-Z_][a-zA-Z0-9_$]*)\s*=/;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Update brace depth
    for (const ch of line) {
      if (ch === '{') depth++;
      else if (ch === '}') depth--;
    }

    if (depth === 0) {
      if (!trimmed.startsWith('//') && !trimmed.startsWith('/*')) {
        const m = trimmed.match(assignRegex);
        if (m && !SCAD_BUILTINS.has(m[1])) {
          lastAssignLine = i + 1; // 1-indexed
        }
      }
    }
  }

  // Insert after the last assignment line, or at line 1 if none exist
  return lastAssignLine === 0 ? 1 : lastAssignLine + 1;
}

// ---------------------------------------------------------------------------
// Parameter annotation builder
// ---------------------------------------------------------------------------

export interface NumberParamConfig {
  kind: 'number';
  min?: number;
  max?: number;
  step?: number;
  isAngle?: boolean;
}

export interface StringParamConfig {
  kind: 'string';
  options?: string[]; // empty / missing → plain text input
}

export interface BooleanParamConfig {
  kind: 'boolean';
}

export interface ColorParamConfig {
  kind: 'color';
}

export interface VectorParamConfig {
  kind: 'vector';
  elementCount: number; // 2 → xypad, 3 → xyzpad, 4+ → shared range
  // Per-axis (for 2 or 3 elements)
  xMin?: number;
  xMax?: number;
  yMin?: number;
  yMax?: number;
  zMin?: number;
  zMax?: number;
  xLabel?: string;
  yLabel?: string;
  zLabel?: string;
  // Shared range (for 4+ elements)
  sharedMin?: number;
  sharedMax?: number;
}

export type ParamConfig =
  | NumberParamConfig
  | StringParamConfig
  | BooleanParamConfig
  | ColorParamConfig
  | VectorParamConfig;

/**
 * Build the inline annotation comment string (without the leading "// ")
 * for a parameter declaration line.
 *
 * Returns an empty string when no annotation is needed (boolean, color, etc.).
 */
export function buildParameterAnnotation(config: ParamConfig): string {
  switch (config.kind) {
    case 'boolean':
    case 'color':
      return '';

    case 'number': {
      if (config.isAngle) {
        const min = config.min ?? 0;
        const max = config.max ?? 360;
        if (
          config.step !== undefined &&
          config.step !== null &&
          config.step !== 0
        ) {
          return `[${min}°:${config.step}°:${max}°]`;
        }
        return `[${min}°:${max}°]`;
      }
      const hasMin = config.min !== undefined && config.min !== null;
      const hasMax = config.max !== undefined && config.max !== null;
      const hasStep =
        config.step !== undefined && config.step !== null && config.step !== 0;
      if (!hasMin && !hasMax) return '';
      const minVal = hasMin ? config.min! : 0;
      const maxVal = hasMax ? config.max! : 100;
      if (hasStep) {
        return `[${minVal}:${config.step}:${maxVal}]`;
      }
      return `[${minVal}:${maxVal}]`;
    }

    case 'string': {
      if (!config.options || config.options.length === 0) return '';
      return `[${config.options.join(', ')}]`;
    }

    case 'vector': {
      const n = config.elementCount;
      if (n === 2) {
        const xMin = config.xMin ?? 0;
        const xMax = config.xMax ?? 100;
        const yMin = config.yMin ?? 0;
        const yMax = config.yMax ?? 100;
        const xl = config.xLabel ? `${config.xLabel} ` : '';
        const yl = config.yLabel ? `${config.yLabel} ` : '';
        return `[${xl}${xMin}:${xMax}, ${yl}${yMin}:${yMax}]`;
      }
      if (n === 3) {
        const xMin = config.xMin ?? 0;
        const xMax = config.xMax ?? 100;
        const yMin = config.yMin ?? 0;
        const yMax = config.yMax ?? 100;
        const zMin = config.zMin ?? 0;
        const zMax = config.zMax ?? 100;
        const xl = config.xLabel ? `${config.xLabel} ` : '';
        const yl = config.yLabel ? `${config.yLabel} ` : '';
        const zl = config.zLabel ? `${config.zLabel} ` : '';
        return `[${xl}${xMin}:${xMax}, ${yl}${yMin}:${yMax}, ${zl}${zMin}:${zMax}]`;
      }
      // 4+ elements: shared range
      const min = config.sharedMin ?? 0;
      const max = config.sharedMax ?? 100;
      return `[${min}:${max}]`;
    }
  }
}

// ---------------------------------------------------------------------------
// Core transformation: applyExtractToVariable
// ---------------------------------------------------------------------------

export interface ExtractToVariableOptions {
  /** The raw literal text that was selected */
  literal: string;
  /** The variable name to introduce */
  varName: string;
  /** 1-indexed line number where the declaration should be inserted */
  insertionLine: number;
  /** Whether to replace all identical occurrences or only the selected one */
  replaceAll: boolean;
  /** 0-indexed character offset of the start of the selection in the original code */
  selectionStartOffset: number;
  /** 0-indexed character offset of the end of the selection (exclusive) in the original code */
  selectionEndOffset: number;
  /** Inline annotation comment (without "// "), or empty/null for no annotation */
  annotation?: string | null;
  /** Optional tooltip text; generates a preceding "// <tooltip>" comment line */
  tooltip?: string | null;
}

/**
 * Apply the "extract to variable" transformation to `code`.
 *
 * Steps:
 *  1. Build the declaration line(s): optional tooltip comment + `varName = literal; [// annotation]`
 *  2. Insert them at `insertionLine` (pushing existing lines down)
 *  3. Replace literal occurrences with `varName` (all or just the selection)
 *     — occurrences inside comments/strings are never replaced
 *
 * Returns the modified source string.
 */
export function applyExtractToVariable(
  code: string,
  opts: ExtractToVariableOptions,
): string {
  const {
    literal,
    varName,
    insertionLine,
    replaceAll,
    selectionStartOffset,
    selectionEndOffset,
    annotation,
    tooltip,
  } = opts;

  // -------------------------------------------------------------------------
  // 1. Build the declaration block
  // -------------------------------------------------------------------------
  const annotationSuffix =
    annotation && annotation.trim() ? ` // ${annotation.trim()}` : '';
  const declarationLine = `${varName} = ${literal};${annotationSuffix}`;
  const tooltipLine = tooltip && tooltip.trim() ? `// ${tooltip.trim()}` : null;

  const insertedLines = tooltipLine
    ? [tooltipLine, declarationLine]
    : [declarationLine];
  const insertedText = insertedLines.join('\n') + '\n';

  // -------------------------------------------------------------------------
  // 2. Insert the declaration at insertionLine (1-indexed)
  // -------------------------------------------------------------------------
  const lines = code.split('\n');
  const insertIdx = Math.max(0, Math.min(insertionLine - 1, lines.length));
  lines.splice(insertIdx, 0, ...insertedLines);
  let newCode = lines.join('\n');

  // -------------------------------------------------------------------------
  // 3. Replace literal occurrences
  //
  // The offsets in the original code have shifted: anything at or after the
  // insertion point is now `insertedText.length` characters further along.
  // We compute the insertion character offset so we can adjust.
  // -------------------------------------------------------------------------

  // Character offset in the original code corresponding to the start of
  // the insertion line (beginning of that line).
  const originalLines = code.split('\n');
  let insertCharOffset = 0;
  for (let i = 0; i < insertIdx && i < originalLines.length; i++) {
    insertCharOffset += originalLines[i].length + 1; // +1 for '\n'
  }

  const shiftIfAfterInsertion = (offset: number): number =>
    offset >= insertCharOffset ? offset + insertedText.length : offset;

  const adjustedSelectionStart = shiftIfAfterInsertion(selectionStartOffset);
  const adjustedSelectionEnd = shiftIfAfterInsertion(selectionEndOffset);

  const regex = buildLiteralRegex(literal);
  const masked = maskCommentsAndStrings(code);
  // Build masked version of the new code for filtering
  const newMasked = maskCommentsAndStrings(newCode);

  if (replaceAll) {
    // Replace all occurrences in original code (excluding comments/strings),
    // then adjust offsets and apply to newCode
    const replacements: Array<{ start: number; end: number }> = [];
    regex.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(code)) !== null) {
      const start = m.index;
      const end = start + m[0].length;
      if (masked[start] !== code[start]) continue; // inside comment/string
      replacements.push({ start, end });
    }

    // Apply replacements from right to left in the new code (with adjusted offsets)
    // to preserve all other offsets.
    const adjustedReplacements = replacements
      .map((r) => ({
        start: shiftIfAfterInsertion(r.start),
        end: shiftIfAfterInsertion(r.end),
      }))
      .sort((a, b) => b.start - a.start); // right to left

    for (const { start, end } of adjustedReplacements) {
      // Double-check it is not inside a comment/string in the new code
      if (newMasked[start] !== newCode[start]) continue;
      newCode = newCode.slice(0, start) + varName + newCode.slice(end);
    }
  } else {
    // Replace only the selected occurrence
    if (newMasked[adjustedSelectionStart] === newCode[adjustedSelectionStart]) {
      newCode =
        newCode.slice(0, adjustedSelectionStart) +
        varName +
        newCode.slice(adjustedSelectionEnd);
    }
  }

  return newCode;
}

// ---------------------------------------------------------------------------

/**
 * Build the complete ExtractPreview from the raw inputs.
 *
 * @param selectedCode        The selected text in the editor
 * @param fullCode            The complete content of the main file
 * @param selectionStartLine  1-indexed start line of the selection
 * @param selectionEndLine    1-indexed end line of the selection
 * @param desiredFilename     The filename the user entered (may or may not end in .scad)
 */
export function buildExtractPreview(
  selectedCode: string,
  fullCode: string,
  selectionStartLine: number,
  selectionEndLine: number,
  desiredFilename: string,
): ExtractPreview {
  const filename = desiredFilename.endsWith('.scad')
    ? desiredFilename
    : `${desiredFilename}.scad`;

  const selectionType = detectSelectionType(selectedCode);

  let usedVars: string[] = [];
  let suggestedModuleName: string;

  if (selectionType === 'module') {
    suggestedModuleName = extractModuleName(selectedCode);
    usedVars = [];
  } else {
    suggestedModuleName = filenameToModuleName(desiredFilename || 'extracted');
    const outerVars = parseOuterVariables(
      fullCode,
      selectionStartLine,
      selectionEndLine,
    );
    usedVars = detectUsedVariables(selectedCode, outerVars);
  }

  const { fileContent, callCode } = buildExtractedContent(
    selectedCode,
    selectionType,
    usedVars,
    suggestedModuleName,
  );

  const importStatement = buildImportStatement(filename);

  return {
    selectionType,
    fileContent,
    callCode,
    importStatement,
    suggestedModuleName,
  };
}
